import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NoteForm } from 'src/app/classes/form-groups/note-form';
import { ViveProgressForm } from 'src/app/classes/form-groups/vive-progress-form';
import { ViveProgress } from 'src/app/classes/models';
import { ComplianceSteps } from 'src/app/interfaces/compliance-steps';
import { ViveProgressStatus } from 'src/app/interfaces/vive-progress-status';
import { ViveProgressSubStatus } from 'src/app/interfaces/vive-progress-substatus';
import { NoteService } from 'src/app/services/note.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ViveProgressService } from 'src/app/services/vive-progress.service';

@Component({
  selector: 'app-add-edit-vive-progress-modal',
  templateUrl: './add-edit-vive-progress-modal.component.html',
  styleUrl: './add-edit-vive-progress-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditViveProgressModalComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private ActiveModal: NgbActiveModal,
    private ViveProgressService: ViveProgressService,
    private UtilityService: UtilityService
  ) {}

  @Input() CompanyVendorId?: number;
  @Input() VendorId?: number;
  @Input() ClientRegisteredVendorId?: number;
  @Input() CompanyId?: number;

  @Input() complianceStep?: ComplianceSteps;
  @Input() ViveProgress?: ViveProgress;

  // Can pass in the 'passing' state of the step to determine if a note is required when
  // setting the status to 'passed'.
  @Input() isStepInPassingState: boolean = false;

  ViveProgressFormGroup = new ViveProgressForm();
  // Note is only required when the sub status is: 'Waived Findings'.
  NoteFormGroup?: NoteForm;

  // If this is used for Business Verified Vendors. We will need to change the statuses.
  Statuses: ViveProgressStatus[] = [
    'Passed',
    'Reviewing',
    'Resolving',
    'Failed'
  ];

  SubStatuses: (ViveProgressSubStatus | null)[] = [];

  ComplianceStepToSubStatusesMap: Map<
    ComplianceSteps,
    (null | ViveProgressSubStatus)[]
  > = new Map([
    [
      'Vendor Info',
      [
        null,
        'Multiple',
        'Public Data Issues',
        'Vendor Agreement Issues',
        'Waived Findings',
        'W-9 Issues'
      ]
    ],
    ['Owner Info', [null, 'Public Data Issues', 'Waived Findings']],
    [
      'Trades & Licenses',
      [null, 'License Issues', 'No Licensed Trades', 'Waived Findings']
    ],
    ['Insurance', [null, 'Coverage Issues', 'Waived Findings']]
  ]);

  statusChanged(status: ViveProgressStatus) {
    if (
      status === 'Passed' &&
      !this.isStepInPassingState &&
      !this.NoteFormGroup
    ) {
      this.NoteFormGroup = new NoteForm();
      this.NoteFormGroup.patchValue({
        title: `${this.complianceStep} Review - Waiver Note`,
        CompanyId: this.CompanyId,
        VendorId: this.VendorId
        //ClientRegisteredVendorId: this.ClientRegisteredVendorId
      });
    }

    if (
      (status !== 'Passed' || this.isStepInPassingState) &&
      this.NoteFormGroup
    ) {
      delete this.NoteFormGroup;
    }
  }

  close() {
    this.ActiveModal.dismiss();
  }

  // Determine whether to save here or return to parent component for save.
  save() {
    const ViveProgressErrors =
      this.UtilityService.generateValidationErrorsObject(
        this.ViveProgressFormGroup
      );

    if (ViveProgressErrors) {
      const { status } = ViveProgressErrors;
      if (status) {
        throw this.ToastService.addErrorToast(
          'Error',
          'A status must be added.',
          { returnError: true }
        );
      }

      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error setting the compliance progress.',
        { returnError: true }
      );
    }

    if (this.NoteFormGroup) {
      const NoteErrors = this.UtilityService.generateValidationErrorsObject(
        this.NoteFormGroup
      );

      if (NoteErrors) {
        throw this.ToastService.addErrorToast(
          'Error',
          'A title and note must be added.',
          { returnError: true }
        );
      }
    }

    const { value: ViveProgress } = this.ViveProgressFormGroup;

    if (this.NoteFormGroup) {
      const { value: Note } = this.NoteFormGroup;

      Object.assign(ViveProgress, { Note });
    }

    return this.ViveProgressService.saveViveProgress(ViveProgress).subscribe({
      next: (SavedViveProgress) => {
        this.ActiveModal.close(SavedViveProgress);
      }
    });
  }

  ngOnInit(): void {
    if (!this.complianceStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance step.',
        { returnError: true }
      );
    }

    if (this.ViveProgress) {
      this.ViveProgressFormGroup.patchValue(this.ViveProgress);
    }

    if (!this.CompanyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor Id.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId && !this.ClientRegisteredVendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (this.VendorId && this.ClientRegisteredVendorId) {
      throw this.ToastService.addErrorToast('Error', 'Multiple Vendor Ids.', {
        returnError: true
      });
    }

    this.ViveProgressFormGroup.patchValue({
      CompanyId: this.CompanyId,
      CompanyVendorId: this.CompanyVendorId,
      VendorId: this.VendorId,
      ClientRegisteredVendorId: this.ClientRegisteredVendorId,
      type: this.complianceStep
    });

    const SubStatuses = this.ComplianceStepToSubStatusesMap.get(
      this.complianceStep
    );
    this.SubStatuses.push(...(SubStatuses || []));

    const {
      value: { status }
    } = this.ViveProgressFormGroup;

    if (status) {
      this.statusChanged(status);
    }
  }
}
